import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { AppData } from '../helpers/app-data';
import { BaseServiceService } from './base-service.service';

/** Respuesta de EnlaceTarea/ValidarOtp. */
export interface SesionEnlace {
  Token: string;
  ExpiraEnUtc: string;
  IdEjecucionProceso: number;
  IdEjecucionTarea: number;
  IdFormulario: number | null;
  IdEjecucionProcesoDetalle: number | null;
}

/** Códigos del backend que cierran el enlace: no hay forma de seguir diligenciando. */
export const CODIGOS_ENLACE_CERRADO = ['LINK_INVALID', 'LINK_EXPIRED', 'LINK_TASK_NOT_AVAILABLE', 'TASK_ALREADY_COMPLETED'];

/**
 * Enlace de formulario expuesto (hallazgo 30): la clave del correo solo sirve para pedir y validar el OTP;
 * el formulario usa una sesión corta (JWT TaskLink) que dura lo mismo que el OTP.
 * Cuando esa sesión vence con el formulario abierto, se pide un OTP nuevo sin perder lo diligenciado.
 */
@Injectable({ providedIn: 'root' })
export class SesionEnlaceService {
  private static readonly CLAVE_STORAGE = 'LinkKey';
  /** Margen para no enviar una petición con un token que vence en el camino. */
  private static readonly MARGEN_SEGUNDOS = 10;

  private readonly solicitandoOtp = new BehaviorSubject<boolean>(false);
  /** true mientras se muestra el modal para retomar la sesión. */
  readonly solicitandoOtp$ = this.solicitandoOtp.asObservable();
  private resolvedores: ((ok: boolean) => void)[] = [];

  constructor(private baseService: BaseServiceService) {}

  get clave(): string | null {
    return sessionStorage.getItem(SesionEnlaceService.CLAVE_STORAGE);
  }

  guardarClave(clave: string): void {
    sessionStorage.setItem(SesionEnlaceService.CLAVE_STORAGE, clave);
  }

  validar(clave: string): Promise<any> {
    return this.baseService.create('EnlaceTarea/Validar', { Clave: clave });
  }

  enviarOtp(clave: string): Promise<any> {
    return this.baseService.create('EnlaceTarea/EnviarOtp', { Clave: clave });
  }

  validarOtp(clave: string, otp: string): Promise<any> {
    return this.baseService.create('EnlaceTarea/ValidarOtp', { Clave: clave, Otp: otp });
  }

  /** Guarda el JWT de la sesión del enlace; lo usan el interceptor y las extensiones del formulario. */
  aplicarSesion(sesion: SesionEnlace): void {
    sessionStorage.setItem(AppData.localStorageToken, sesion.Token);
    const datos: any = jwtDecode(sesion.Token);
    this.baseService.setUsername(datos['name']);
    this.baseService.setUserRol(datos['UserRol']);
  }

  esSesionEnlace(): boolean {
    return SesionEnlaceService.datosToken()?.TokenType === 'TaskLink';
  }

  sesionVigente(): boolean {
    const exp = SesionEnlaceService.datosToken()?.exp;
    return !!exp && exp - SesionEnlaceService.MARGEN_SEGUNDOS > Date.now() / 1000;
  }

  /**
   * Antes de una operación que no debe perderse (guardar el formulario): si la sesión del enlace venció,
   * muestra el modal de OTP y espera. Devuelve false si el usuario no pudo retomar la sesión.
   */
  asegurarSesion(): Promise<boolean> {
    if (!this.esSesionEnlace() || this.sesionVigente()) {
      return Promise.resolve(true);
    }
    return this.solicitarOtp();
  }

  solicitarOtp(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.resolvedores.push(resolve);
      this.solicitandoOtp.next(true);
    });
  }

  /** Lo llama el modal: con ok=true la sesión ya se renovó con aplicarSesion. */
  completarSolicitud(ok: boolean): void {
    const pendientes = this.resolvedores;
    this.resolvedores = [];
    this.solicitandoOtp.next(false);
    pendientes.forEach(resolve => resolve(ok));
  }

  private static datosToken(): any {
    const token = sessionStorage.getItem(AppData.localStorageToken);
    if (!token) {
      return null;
    }
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }
}
